import * as React from 'react';
import styled from 'styled-components';
import { variant } from 'styled-system';

import { useFromScreens } from '../../../../hooks/useDeviceQuery.hook';
import { BaseCard, BaseCardProps, MediaModel } from '../BaseCard/BaseCard';
import { Description } from '../components/Description';
import { Meta, MetadataModel } from '../components/Meta';
import { Title, TitleProps } from '../components/Title';

const Layout = styled.div<{ directional?: 'vertical' | 'horizontal'; rtl?: boolean }>`
  ${(p) =>
    variant({
      prop: 'directional',
      variants: {
        vertical: {
          marginTop: 12,
        },
        horizontal: {
          [p.rtl ? 'marginRight' : 'marginLeft']: 12,
        },
      },
    })}
`;

interface ThumbnailModel {
  url?: string;
  alt?: string;
}

export interface TextOnlyCardProps {
  title?: string;
  description?: string;
  thumbnail?: ThumbnailModel;
  url?: string;
  metadata?: MetadataModel[];
  directional?: BaseCardProps['directional'];
  rtl?: BaseCardProps['rtl'];

  withoutThumbnail?: boolean;
}

export const TextOnlyCard = (props: TextOnlyCardProps) => {
  const {
    title,
    description,
    metadata,
    thumbnail,
    url,
    directional = 'vertical',
    rtl = false,
    withoutThumbnail = false,
  } = props;
  const [from552, from750, from1080] = useFromScreens([552, 750, 1080]);
  const isHorizontal = directional === 'horizontal';

  const withMetadata = Boolean(metadata?.length);
  const withDescription = !(!from552 && isHorizontal);

  const media = React.useMemo<MediaModel>(() => {
    if (withoutThumbnail) return null;
    let additionProps = {};
    if (isHorizontal) {
      additionProps = {
        width: 100,
        ratio: 1,
      };

      if (rtl) {
        additionProps = {
          width: 100,
          ratio: 1,
        };
        if (from552) {
          additionProps = {
            width: 150,
            ratio: 3 / 2,
          };
        }
        if (from750) {
          additionProps = {
            width: 200,
            ratio: 100 / 67, // height = 134
          };
        }
        if (from1080) {
          additionProps = {
            width: 100,
            ratio: 1,
          };
        }
      }
    }

    return {
      type: 'image',
      alt: thumbnail?.alt || title,
      src: thumbnail?.url || '',
      rounded: true,
      ...additionProps,
    };
  }, [
    from1080,
    from552,
    from750,
    isHorizontal,
    rtl,
    thumbnail?.alt,
    thumbnail?.url,
    title,
    withoutThumbnail,
  ]);

  const titleProps = React.useMemo<TitleProps>(() => {
    if (isHorizontal) {
      if (rtl) {
        return {
          typoVariant: 'semi-bold/16',
          typoVariantTablet: 'semi-bold/20',
          mt: '8px',
          mb: '4px',
        };
      }

      return {
        typoVariant: 'semi-bold/16',
        mt: '8px',
        mb: '4px',
      };
    }

    return {
      typoVariant: 'semi-bold/16',
      typoVariantTablet: 'semi-bold/20',
      mt: '8px',
      mb: '12px',
    };
  }, [isHorizontal, rtl]);

  return (
    <BaseCard directional={directional} media={media} rtl={rtl} url={url}>
      <Layout directional={directional} rtl={rtl}>
        {withMetadata && <Meta metadata={metadata} />}

        <Title
          url={url}
          text={title}
          {...titleProps}
          ellipsis={{ rows: !withDescription && rtl ? 4 : 2 }}
        />
        {withDescription && (
          <Description text={description} ellipsis={{ rows: isHorizontal ? 2 : 3 }} />
        )}
      </Layout>
    </BaseCard>
  );
};
